import React, { createContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToast } from "hooks";
import {
  AI_VIDEO_INTERVIEW_CONFIG,
  AI_VIDEO_TASK_DISPLAY_STATE,
  getVideoTaskDisplayState,
  isVideoTaskConfirmed,
  isVideoTaskErrored,
  isVideoTaskPollingState,
} from "constants/index";
import { GET_INTERVIEW_QUESTION_AI_VIDEO_GENERATION_STATUS } from "graphql/queries";
import {
  CANCEL_INTERVIEW_QUESTION_AI_VIDEO_GENERATION,
  CONFIRM_INTERVIEW_QUESTION_AI_VIDEO_GENERATION,
  SUBMIT_INTERVIEW_QUESTION_AI_VIDEO_GENERATION,
} from "graphql/mutations";

const InterviewQuestionAIVideoContext = createContext(null);
const createCompletionState = ({ shouldShowAttachSuccess = false } = {}) => ({ shouldShowAttachSuccess });
const createQuestionFlagState = (previous, questionId, value) => ({ ...previous, [questionId]: value });
const clearCompletionState = (ref, questionId) => delete ref.current[questionId];

/**
 * InterviewQuestionAIVideoProvider
 * Provides shared state and functionality for managing AI video generation for interview questions across components.
 * It handles the logic for starting video generation, polling for status updates, confirming generated videos, and canceling tasks.
 * The provider also manages the state for modals related to AI video actions and previews.
 *
 * @param {children}      children             The child components that will consume the context.
 * @param {array}         questions            The list of interview questions, each potentially containing AI video generation task data.
 * @param {function}      refetchInterview     Function to refetch the interview data, typically after a video generation task is completed or confirmed.
 */
const InterviewQuestionAIVideoProvider = ({ children, questions = [], refetchInterview }) => {
  const { toast } = useToast();
  const [tasksByQuestionId, setTasksByQuestionId] = useState({});
  const [aiVideoQuestion, setAiVideoQuestion] = useState(null);
  const [showAiVideoModal, setShowAiVideoModal] = useState(false);
  const [videoActionQuestion, setVideoActionQuestion] = useState(null);
  const [showVideoActionModal, setShowVideoActionModal] = useState(false);
  const [submittingNewAttemptByQuestionId, setSubmittingNewAttemptByQuestionId] = useState({});
  const pollingRefs = useRef({});
  const completionStateRef = useRef({});

  const [getStatus] = useLazyQuery(GET_INTERVIEW_QUESTION_AI_VIDEO_GENERATION_STATUS, { fetchPolicy: "network-only" });
  const [submitGeneration, { loading: submitGenerationLoading }] = useMutation(
    SUBMIT_INTERVIEW_QUESTION_AI_VIDEO_GENERATION
  );
  const [confirmGeneration, { loading: confirmGenerationLoading }] = useMutation(
    CONFIRM_INTERVIEW_QUESTION_AI_VIDEO_GENERATION
  );
  const [cancelGeneration, { loading: cancelGenerationLoading }] = useMutation(
    CANCEL_INTERVIEW_QUESTION_AI_VIDEO_GENERATION
  );

  useEffect(() => {
    setTasksByQuestionId((prev) => {
      const next = { ...prev };

      questions.forEach((question) => {
        if (question?.aiVideoGenerationTask) {
          next[question.id] = {
            ...next[question.id],
            ...question.aiVideoGenerationTask,
          };
        } else if (Object.prototype.hasOwnProperty.call(next, question.id)) {
          next[question.id] = null;
        }
      });

      return next;
    });
  }, [questions]);

  useEffect(() => {
    Object.entries(tasksByQuestionId).forEach(([questionId, task]) => {
      if (task && isVideoTaskPollingState(task.status) && !pollingRefs.current[questionId]) {
        startPolling(questionId);
      }
    });
  }, [tasksByQuestionId]);

  useEffect(
    () => () => {
      Object.values(pollingRefs.current).forEach(clearInterval);
      pollingRefs.current = {};
    },
    []
  );

  const setTask = (questionId, task) => setTasksByQuestionId((prev) => ({ ...prev, [questionId]: task }));

  const setSubmittingNewAttempt = (questionId, value) =>
    setSubmittingNewAttemptByQuestionId((prev) => createQuestionFlagState(prev, questionId, value));

  const clearTask = (questionId) =>
    setTasksByQuestionId((prev) => {
      const next = { ...prev };
      next[questionId] = null;
      return next;
    });

  const stopPolling = (questionId) => {
    if (pollingRefs.current[questionId]) {
      clearInterval(pollingRefs.current[questionId]);
      delete pollingRefs.current[questionId];
    }
  };

  const clearTaskLifecycleState = (questionId) => {
    stopPolling(questionId);
    clearCompletionState(completionStateRef, questionId);
  };

  const handleTaskCompletion = (questionId) => {
    completionStateRef.current[questionId] = createCompletionState();
    toast.success(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.GenerationCompleted" />);
    stopPolling(questionId);
  };

  const handleTaskError = (questionId, task) => {
    setTask(questionId, task);
    clearTaskLifecycleState(questionId);
    toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Error" />);
  };

  const handleMissingTask = (questionId) => {
    stopPolling(questionId);

    if (completionStateRef.current[questionId]?.shouldShowAttachSuccess) {
      refetchInterview?.();
      closeAiVideoModal();
      toast.success(
        <FormattedMessage
          id="Missions.Roles.RoleVetting.Interviews.AIVideo.Attach.Success"
          defaultMessage="Confirmed video has been uploaded and attached"
        />
      );
    }

    clearCompletionState(completionStateRef, questionId);
    clearTask(questionId);
  };

  const handlePolledTask = (questionId, task) => {
    setTask(questionId, {
      ...task,
      refreshedAt: new Date().toISOString(),
    });

    if (isVideoTaskErrored(task)) {
      handleTaskError(questionId, task);
      return;
    }

    const uiState = getVideoTaskDisplayState(task);

    if (uiState === AI_VIDEO_TASK_DISPLAY_STATE.REVIEW) {
      handleTaskCompletion(questionId);
      return;
    }

    if (isVideoTaskConfirmed(task?.status)) {
      completionStateRef.current[questionId] = createCompletionState({ shouldShowAttachSuccess: true });
    }
  };

  const startPolling = (questionId) => {
    stopPolling(questionId);

    const poll = async () => {
      try {
        const { data, error } = await getStatus({ variables: { questionId } });
        const task = data?.interviewQuestionAiVideoGenerationStatus;

        if (error) {
          stopPolling(questionId);
          toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Error" />);
          return;
        }

        if (!task) {
          handleMissingTask(questionId);
          return;
        }

        handlePolledTask(questionId, task);
      } catch {
        stopPolling(questionId);
        toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Error" />);
      }
    };

    pollingRefs.current[questionId] = setInterval(poll, AI_VIDEO_INTERVIEW_CONFIG.POLLING_INTERVAL);
    poll();
  };

  const openVideoActionModal = (question) => {
    const task = Object.prototype.hasOwnProperty.call(tasksByQuestionId, question.id)
      ? tasksByQuestionId[question.id]
      : question?.aiVideoGenerationTask;

    if (task) {
      setAiVideoQuestion(question);
      setShowAiVideoModal(true);

      if (isVideoTaskPollingState(task.status)) {
        setTask(question.id, task);
        startPolling(question.id);
      }

      return;
    }

    setVideoActionQuestion(question);
    setShowVideoActionModal(true);
  };

  const closeVideoActionModal = () => {
    setShowVideoActionModal(false);
    setVideoActionQuestion(null);
  };

  const reopenVideoActionModal = (question, options = {}) => {
    setShowAiVideoModal(false);
    setAiVideoQuestion(null);
    setVideoActionQuestion({ ...question, ...options });
    setShowVideoActionModal(true);
  };

  const openAiVideoModal = async (question, { ignoreExistingTask = false } = {}) => {
    setShowVideoActionModal(false);
    setVideoActionQuestion(null);
    setAiVideoQuestion(question);
    setShowAiVideoModal(true);

    const existingTask = Object.prototype.hasOwnProperty.call(tasksByQuestionId, question.id)
      ? tasksByQuestionId[question.id]
      : question?.aiVideoGenerationTask;

    if (existingTask && !ignoreExistingTask) {
      setTask(question.id, existingTask);
      if (isVideoTaskPollingState(existingTask.status)) {
        startPolling(question.id);
      }
      return;
    }

    setSubmittingNewAttempt(question.id, true);

    try {
      const { data } = await submitGeneration({
        variables: { input: { questionId: question.id } },
        onCompleted: () => {
          closeAiVideoModal();
          toast.success(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.GenerationStarted" />);
        },
        onError: () => {
          closeAiVideoModal();
          toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Error" />);
        },
      });

      const { task, interviewQuestion } = data?.submitInterviewQuestionAiVideoGeneration || {};

      if (task) {
        setTask(question.id, interviewQuestion?.aiVideoGenerationTask || task);
        setSubmittingNewAttempt(question.id, false);
        startPolling(question.id);
        return;
      }

      setSubmittingNewAttempt(question.id, false);
      clearTask(question.id);
      toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Error" />);
    } catch {
      setSubmittingNewAttempt(question.id, false);
      clearTask(question.id);
      toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Error" />);
    }
  };

  const closeAiVideoModal = () => {
    setShowAiVideoModal(false);
    setAiVideoQuestion(null);
  };

  const confirmTask = async (questionId) =>
    await confirmGeneration({
      variables: { input: { questionId } },
      onCompleted: ({ confirmInterviewQuestionAiVideoGeneration }) => {
        const task = confirmInterviewQuestionAiVideoGeneration?.interviewQuestion?.aiVideoGenerationTask;

        if (task) {
          setTask(questionId, task);
          completionStateRef.current[questionId] = createCompletionState({ shouldShowAttachSuccess: true });
          startPolling(questionId);
        }

        toast.success(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Attach.Started" />);
      },
      onError: () => toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Attach.Error" />),
    });

  const cancelTask = async (questionId) =>
    await cancelGeneration({
      variables: { input: { questionId } },
      onCompleted: () => {
        clearTaskLifecycleState(questionId);
        clearTask(questionId);
        toast.success(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Discard.Success" />);
      },
      onError: () => toast.error(<FormattedMessage id="Missions.Roles.RoleVetting.Interviews.AIVideo.Discard.Error" />),
    });

  const value = useMemo(
    () => ({
      tasksByQuestionId,
      getTask: (questionId) =>
        Object.prototype.hasOwnProperty.call(tasksByQuestionId, questionId) ? tasksByQuestionId[questionId] : undefined,
      aiVideoQuestion,
      showAiVideoModal,
      videoActionQuestion,
      showVideoActionModal,
      isSubmittingNewAttempt: (questionId) => Boolean(submittingNewAttemptByQuestionId[questionId]),
      openVideoActionModal,
      reopenVideoActionModal,
      closeVideoActionModal,
      openAiVideoModal,
      closeAiVideoModal,
      confirmTask,
      cancelTask,
      loading: submitGenerationLoading || confirmGenerationLoading || cancelGenerationLoading,
    }),
    [
      tasksByQuestionId,
      aiVideoQuestion,
      showAiVideoModal,
      videoActionQuestion,
      showVideoActionModal,
      submittingNewAttemptByQuestionId,
      submitGenerationLoading,
      confirmGenerationLoading,
      cancelGenerationLoading,
    ]
  );

  return <InterviewQuestionAIVideoContext.Provider value={value}>{children}</InterviewQuestionAIVideoContext.Provider>;
};

InterviewQuestionAIVideoProvider.propTypes = {
  children: PropTypes.node.isRequired,
  questions: PropTypes.array,
  refetchInterview: PropTypes.func,
};

export default InterviewQuestionAIVideoProvider;

export { InterviewQuestionAIVideoContext };
